import { useState, useCallback, ReactNode, ReactElement, MouseEvent } from 'react';
import { UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import {
  Button,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  ButtonProps,
  useTheme,
  MenuItemProps,
  MenuListProps,
  PopperPlacementType,
  styled,
} from '@mui/material';
import { isDarkMode } from '@localstack/services';

const StyledMenuList = styled(MenuList)(({ theme }) => ({
  '& .MuiMenuItem-root': {
    borderRadius: '6px',
    padding: theme.spacing(1.2),
    margin: `0 ${theme.spacing(0.5)}`,
    lineHeight: '1.2em',
    '&:hover': {
      backgroundColor: isDarkMode(theme) ? theme.palette.action.hover : theme.palette.grey[100],
    },
    '& + .MuiDivider-root': {
      margin: `${theme.spacing(0.5)} 0`,
    },
  },
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'anchorWidth',
})<{ anchorWidth?: number }>(({ theme, anchorWidth }) => ({
  minWidth: anchorWidth,
  borderRadius: 12,
  maxHeight: 500,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: `${theme.spacing(0.5)} 0`,
}));

export type DropdownProps = MenuItemProps &
  ButtonProps & {
    label?: string | ReactNode;
    renderButton?: (props: DropdownProps) => JSX.Element;
    children: ReactNode | ((onClose: () => void) => ReactNode);
    stopPropagation?: boolean;
    dropdownClassName?: string;
    menuListProps?: MenuListProps;
    placement?: PopperPlacementType;
    offset?: [number, number];
  };

export const Dropdown = ({
  label,
  renderButton,
  children,
  stopPropagation,
  dropdownClassName,
  menuListProps,
  placement,
  offset,
  ...rest
}: DropdownProps): ReactElement => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<Optional<Element>>(null);
  const [anchorWidth, setAnchorWidth] = useState<number>();

  const handleClick = useCallback((event: MouseEvent<Element>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(event.currentTarget);
    setAnchorWidth(event.currentTarget.clientWidth);
  }, []);

  return (
    <>
      {renderButton && renderButton({ label, children, onClick: handleClick, ...rest })}
      {!renderButton && (
        <>
          {label && (
            <Button onClick={handleClick} variant="outlined" disableElevation endIcon={<UnfoldMoreIcon />} {...rest}>
              {label}
            </Button>
          )}
          {!label && (
            <Button onClick={handleClick} variant="outlined" disableElevation {...rest}>
              <UnfoldMoreIcon />
            </Button>
          )}
        </>
      )}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        transition
        style={{ zIndex: theme.zIndex.tooltip }}
        placement={placement}
        modifiers={offset ? [{ name: 'offset', options: { offset } }] : undefined}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <StyledPaper className={dropdownClassName} anchorWidth={anchorWidth}>
              <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => setAnchorEl(null)}>
                <StyledMenuList autoFocusItem={Boolean(anchorEl)} disablePadding {...menuListProps}>
                  {typeof children === 'function' ? children(() => setAnchorEl(null)) : children}
                </StyledMenuList>
              </ClickAwayListener>
            </StyledPaper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
